export interface IPlanFeature {
  label: string;
  note?: string;
  highlight?: boolean;
  highlightColor?: 'primary' | 'green';
}

export interface ILockedFeature {
  label: string;
  requiresPlan: string;
}

export interface IPlanMeta {
  tier: 'starter' | 'growth' | 'portfolio' | 'enterprise' | 'generic';
  featured: boolean;
  dark: boolean;
  scaleLimitLabel: string;
  perAppBase: number | null;
  valueBridgeTitle?: string;
  valueBridgeBody?: string;
  dataParams: { pull: string; window: string };
  features: {
    checked: IPlanFeature[];
    locked: ILockedFeature[];
  };
  featureHeading: string;
  ctaStyle: 'ghost' | 'primary' | 'green' | 'dark';
  enterpriseContact?: boolean;
  topBarGradient: string;
  badgeBg: string;
  badgeColor: string;
  badgeBorder: string;
  scaleBg: string;
  scaleLabelColor: string;
  scaleValueColor: string;
}

export const PLAN_META: Record<string, IPlanMeta> = {
  starter: {
    tier: 'starter',
    featured: false,
    dark: false,
    scaleLimitLabel: '5 Apps',
    perAppBase: 3.0,
    dataParams: { pull: '30 days', window: '90 days' },
    features: {
      checked: [
        { label: 'True ROI dashboard', note: 'AdMob + IAP + spend' },
        { label: 'All ad networks — Google, Meta, Apple' },
        { label: 'App Store + Play Console sync' },
        { label: 'IAP + Subscription revenue' },
        { label: 'Campaign management' },
        { label: 'Country-wise reports' },
        { label: 'Daily data sync' },
        { label: 'Anomaly alerts — email' },
        { label: '1 read-only team seat' },
        { label: 'Email support' },
      ],
      locked: [
        { label: 'AI-Driven Reports', requiresPlan: 'Growth+' },
        { label: 'SMS + in-app alerts', requiresPlan: 'Growth+' },
        { label: 'Team management', requiresPlan: 'Portfolio+' },
      ],
    },
    featureHeading: "What's included",
    ctaStyle: 'ghost',
    topBarGradient: 'linear-gradient(90deg, #6c8dc0, #1a3d7c)',
    badgeBg: '#eef2fc',
    badgeColor: '#1a3d7c',
    badgeBorder: '#bfcfe8',
    scaleBg: 'linear-gradient(135deg, #eef2fc, #e2eaf8)',
    scaleLabelColor: '#2a5ba8',
    scaleValueColor: '#1a3d7c',
  },

  growth: {
    tier: 'growth',
    featured: false,
    dark: false,
    scaleLimitLabel: '20 Apps',
    perAppBase: 2.5,
    valueBridgeTitle: 'Why the upgrade pays off',
    valueBridgeBody: '20 apps + AI Reports = $2.50/app. AI alone replaces 4+ hrs of manual analysis weekly.',
    dataParams: { pull: '90 days', window: '180 days' },
    features: {
      checked: [
        { label: 'AI-Driven Reports — unlocked', highlight: true, highlightColor: 'primary' },
        { label: 'Anomaly alerts — email + SMS + in-app', highlight: true, highlightColor: 'green' },
        { label: 'Advanced ROI + mediation splits' },
        { label: 'Play Store Order Management' },
        { label: '2 full team seats' },
        { label: 'Email + chat support' },
      ],
      locked: [
        { label: 'Team management (5 seats)', requiresPlan: 'Portfolio+' },
        { label: 'White-label exports', requiresPlan: 'Portfolio+' },
      ],
    },
    featureHeading: 'Everything in Starter, plus',
    ctaStyle: 'green',
    topBarGradient: 'linear-gradient(90deg, #1a3d7c, #30cd73)',
    badgeBg: '#f0fdf4',
    badgeColor: '#166534',
    badgeBorder: '#86efac',
    scaleBg: 'linear-gradient(135deg, #f0fdf4, #dcfce7)',
    scaleLabelColor: '#166534',
    scaleValueColor: '#15803d',
  },

  portfolio: {
    tier: 'portfolio',
    featured: true,
    dark: false,
    scaleLimitLabel: '100 Apps',
    perAppBase: 1.5,
    valueBridgeTitle: 'The team math',
    valueBridgeBody: '5 seats + 100 apps = $30/person/mo. Finance approves this in one email.',
    dataParams: { pull: '180 days', window: '1 year' },
    features: {
      checked: [
        { label: 'Team Management — 5 full seats', highlight: true, highlightColor: 'primary' },
        { label: '1 full year of live dashboard data', highlight: true, highlightColor: 'green' },
        { label: 'White-label report exports' },
        { label: 'Custom anomaly thresholds' },
        { label: 'Stakeholder report exports' },
        { label: 'Dedicated onboarding session' },
        { label: 'Priority email + chat support' },
      ],
      locked: [],
    },
    featureHeading: 'Everything in Growth, plus',
    ctaStyle: 'primary',
    topBarGradient: 'linear-gradient(90deg, #1a3d7c, #2a5ba8)',
    badgeBg: 'linear-gradient(135deg, #dde6f5, #eef2fc)',
    badgeColor: '#1a3d7c',
    badgeBorder: '#8aadd6',
    scaleBg: 'linear-gradient(135deg, #eef2fc, #dde6f5)',
    scaleLabelColor: '#2a5ba8',
    scaleValueColor: '#1a3d7c',
  },

  enterprise: {
    tier: 'enterprise',
    featured: false,
    dark: true,
    scaleLimitLabel: 'Unlimited',
    perAppBase: null,
    dataParams: { pull: '1 year', window: '2 years' },
    features: {
      checked: [
        { label: 'Unlimited apps, seats & accounts' },
        { label: 'Full history & 2-year live window' },
        { label: 'Dedicated CSM + custom SLA' },
        { label: 'White-glove onboarding' },
        { label: 'Custom contract & billing terms' },
      ],
      locked: [],
    },
    featureHeading: 'Everything in Portfolio, plus',
    ctaStyle: 'dark',
    enterpriseContact: true,
    topBarGradient: 'linear-gradient(90deg, #30cd73, #1a3d7c)',
    badgeBg: 'rgba(48,205,115,0.15)',
    badgeColor: '#30cd73',
    badgeBorder: 'rgba(48,205,115,0.25)',
    scaleBg: 'rgba(255,255,255,0.06)',
    scaleLabelColor: 'rgba(255,255,255,0.40)',
    scaleValueColor: '#ffffff',
  },

  generic: {
    tier: 'generic',
    featured: false,
    dark: false,
    scaleLimitLabel: '—',
    perAppBase: null,
    dataParams: { pull: '—', window: '—' },
    features: {
      checked: [
        { label: 'Full platform access' },
        { label: 'All ad networks' },
        { label: 'Campaign management' },
        { label: 'Email support' },
      ],
      locked: [],
    },
    featureHeading: "What's included",
    ctaStyle: 'primary',
    topBarGradient: 'linear-gradient(90deg, #6c8dc0, #1a3d7c)',
    badgeBg: '#eef2fc',
    badgeColor: '#1a3d7c',
    badgeBorder: '#bfcfe8',
    scaleBg: 'linear-gradient(135deg, #eef2fc, #e2eaf8)',
    scaleLabelColor: '#2a5ba8',
    scaleValueColor: '#1a3d7c',
  },
};

// Index-based fallback order when plan name doesn't match any known tier
const INDEX_TIERS = ['starter', 'growth', 'portfolio', 'enterprise'] as const;

export const getPlanMeta = (planName: string, planIndex?: number): IPlanMeta => {
  const key = planName.toLowerCase().trim();
  // 1. Direct exact match
  if (PLAN_META[key]) return PLAN_META[key];
  // 2. Partial substring match ("Starter Plan", "Growth - Monthly", "New Starter Plan")
  const found = Object.keys(PLAN_META).find(k => k !== 'generic' && key.includes(k));
  if (found) return PLAN_META[found];
  // 3. Index-based fallback — assign by position in the plans array
  if (planIndex !== undefined && planIndex < INDEX_TIERS.length) {
    return PLAN_META[INDEX_TIERS[planIndex]];
  }
  return PLAN_META['generic'];
};
